import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  FormHelperText
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { ApiService } from '../../services/ApiService';
import { usePermissions } from '../../hooks/usePermissions';
import { Permission } from '../../types/user';
import { useAuth } from '../../context/SimpleAuthContext';

interface Task {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'in-progress' | 'completed' | 'blocked';
  priority: 'low' | 'medium' | 'high' | 'critical';
  assignee: string;
  dueDate: string;
  requirementId: string;
  requirementTitle?: string;
  metadata?: any;
}

// Status transition rules
const STATUS_TRANSITIONS: Record<Task['status'], Task['status'][]> = {
  'pending': ['in-progress', 'blocked'],
  'in-progress': ['completed', 'blocked', 'pending'],
  'blocked': ['pending', 'in-progress'],
  'completed': ['in-progress'] // Allow reopening completed tasks
};

export default function MaterialTaskEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [statusWarning, setStatusWarning] = useState<string | null>(null);
  
  const canEdit = hasPermission(Permission.TASK_EDIT) || 
    (hasPermission(Permission.TASK_EDIT_OWN) && task?.assignee === user?.name);

  useEffect(() => {
    fetchTask();
  }, [id]);

  const fetchTask = async () => {
    try {
      setLoading(true);
      const response = await ApiService.get<Task>(`/tasks/${id}`);
      setTask(response);
    } catch (error) {
      console.error('Error fetching task:', error);
      setError('Failed to load task details');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof Task) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!task) return;
    
    const value = event.target.value;
    
    // Special handling for status changes
    if (field === 'status') {
      const newStatus = value as Task['status'];
      const currentStatus = task.status;
      const allowedTransitions = STATUS_TRANSITIONS[currentStatus];
      
      if (!allowedTransitions.includes(newStatus)) {
        setStatusWarning(`Cannot transition from "${currentStatus}" to "${newStatus}". Allowed transitions: ${allowedTransitions.join(', ')}`);
        return;
      } else {
        setStatusWarning(null);
      }
    }
    
    setTask({
      ...task,
      [field]: value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!task) return;
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      // Validate the task
      if (!task.title || !task.description) {
        setError('Title and description are required');
        return;
      }
      
      // Check status transition one more time
      const originalTask = await ApiService.get<Task>(`/tasks/${id}`);
      const allowedTransitions = STATUS_TRANSITIONS[originalTask.status];
      
      if (task.status !== originalTask.status && !allowedTransitions.includes(task.status)) {
        setError(`Invalid status transition from "${originalTask.status}" to "${task.status}"`);
        return;
      }
      
      // Update the task
      await ApiService.put(`/tasks/${id}`, {
        title: task.title,
        description: task.description,
        status: task.status,
        priority: task.priority,
        assignee: task.assignee,
        dueDate: task.dueDate,
        requirementId: task.requirementId
      });
      
      setSuccess('Task updated successfully');
      
      // Redirect back to task list after a short delay
      setTimeout(() => {
        navigate('/tasks');
      }, 1500);
      
    } catch (error: any) {
      console.error('Error updating task:', error);
      setError(error.message || 'Failed to update task');
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status: Task['status']) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in-progress':
        return 'primary';
      case 'blocked':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: Task['priority']) => {
    switch (priority) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'primary';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!task) {
    return (
      <Box>
        <Alert severity="error">Task not found</Alert>
      </Box>
    );
  }

  if (!canEdit) {
    return (
      <Box>
        <Alert severity="error">You don't have permission to edit this task</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Edit Task
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {statusWarning && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningIcon />}>
          {statusWarning}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                id="task-title-input"
                fullWidth
                label="Task Title"
                value={task.title}
                onChange={handleChange('title')}
                required
                disabled={saving}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                id="task-description-input"
                fullWidth
                multiline
                rows={4}
                label="Description"
                value={task.description}
                onChange={handleChange('description')}
                required
                disabled={saving}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                id="task-status-select"
                select
                fullWidth
                label="Status"
                value={task.status}
                onChange={handleChange('status')}
                disabled={saving}
                error={!!statusWarning}
              >
                {Object.entries(STATUS_TRANSITIONS[task.status] || {}).map(([_, allowedStatus]) => (
                  <MenuItem key={allowedStatus} value={allowedStatus}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip 
                        label={allowedStatus.replace('-', ' ')} 
                        color={getStatusColor(allowedStatus)} 
                        size="small" 
                      />
                    </Box>
                  </MenuItem>
                ))}
                <MenuItem value={task.status}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip 
                      label={`${task.status.replace('-', ' ')} (current)`} 
                      color={getStatusColor(task.status)} 
                      size="small" 
                    />
                  </Box>
                </MenuItem>
              </TextField>
              {statusWarning && (
                <FormHelperText error>
                  Valid transitions: {STATUS_TRANSITIONS[task.status].join(', ')}
                </FormHelperText>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                id="task-priority-select"
                select
                fullWidth
                label="Priority"
                value={task.priority}
                onChange={handleChange('priority')}
                disabled={saving}
              >
                <MenuItem value="low">
                  <Chip label="Low" color="default" size="small" />
                </MenuItem>
                <MenuItem value="medium">
                  <Chip label="Medium" color="primary" size="small" />
                </MenuItem>
                <MenuItem value="high">
                  <Chip label="High" color="warning" size="small" />
                </MenuItem>
                <MenuItem value="critical">
                  <Chip label="Critical" color="error" size="small" />
                </MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                id="task-assignee-input"
                fullWidth
                label="Assignee"
                value={task.assignee}
                onChange={handleChange('assignee')}
                disabled={saving}
                placeholder="Enter assignee name"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                id="task-due-date-input"
                fullWidth
                type="date"
                label="Due Date"
                value={task.dueDate}
                onChange={handleChange('dueDate')}
                disabled={saving}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => navigate('/tasks')}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  disabled={saving || !!statusWarning}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
}
